import React, { useEffect, useRef, useState } from 'react';
import { getBaseURL } from '../../config/runtime';
import { Plan } from '../../domain/product/plan';
import { fetchJson } from '../../util/fetch';
import { ButtonSubmit } from './button/Button';
import { FormPanel } from './form_panel';
import { Label } from './input/Label';
import { LabelValidation } from './label_validation';

export interface Props {
  onPlanSelected: (plan: Plan) => void;
}

export const FormAddProductPlan = (props: Props) => {
  const [planName, setPlanName] = useState('');
  const [numLevels, setNumLevels] = useState(0);

  const [planNameValidation, setPlanNameValidation] = useState('');
  const [showPlanNameValidation, setShowPlanNameValidation] = useState(false);
  const [showNumLevelsValidation, setShowNumLevelsValidation] = useState(false);

  const firstUpdate = useRef(true);

  const validatePlanName = () => {
    const validPlanName = planName.length > 0;
    if (!validPlanName) {
      setPlanNameValidation('Please enter a plan name.');
    }
    setShowPlanNameValidation(!validPlanName);
    return validPlanName;
  };

  const validateNumLevels = () => {
    const validNumLevels = numLevels > 0;
    setShowNumLevelsValidation(!validNumLevels);
    return validNumLevels;
  };

  useEffect(() => {
    if (!firstUpdate.current) {
      validatePlanName();
    }
  }, [planName]);

  useEffect(() => {
    if (!firstUpdate.current) {
      validateNumLevels();
    }
  }, [numLevels]);

  useEffect(() => {
    firstUpdate.current = false;
  }, []);

  const validate = () => {
    const validPlanName = validatePlanName();
    const validNumLevels = validateNumLevels();
    return validPlanName && validNumLevels;
  };

  const addProductPlan = async () => {
    const respPromise = fetchJson<{ object: string } & Plan>({
      url: `${getBaseURL()}/v1/client/plans`,
      options: {
        method: 'POST',
        body: JSON.stringify({
          name: planName,
          num_levels: numLevels,
          active: true,
        }),
        headers: new Headers({
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        }),
      },
    });
    respPromise
      .then((resp) => {
        if (resp.object === 'plan') {
          props.onPlanSelected(resp);
        }
      })
      .catch((e) => {
        if (e.response?.status === 409) {
          setPlanNameValidation('A plan with this name already exists.');
          setShowPlanNameValidation(true);
        }
      });
  };

  const submit = () => {
    if (validate()) {
      addProductPlan();
    }
  };

  return (
    <FormPanel>
      <Label>Plan Name</Label>
      <input
        onChange={(e) => setPlanName(e.target.value)}
        style={{ marginBottom: '.15rem', width: '25rem' }}
        autoFocus={true}
      />
      <LabelValidation
        show={showPlanNameValidation}
        style={{ marginBottom: '.5rem' }}
      >
        {planNameValidation}
      </LabelValidation>

      <Label>Number of levels</Label>
      <select
        id="levels"
        onChange={(e) => setNumLevels(parseInt(e.target.value))}
        style={{ marginBottom: '.15rem' }}
      >
        <option key="select" value={0}>
          Select...
        </option>
        <option key={1} value={1}>
          1 Level
        </option>
        <option key={2} value={2}>
          2 Levels
        </option>
        <option key={3} value={3}>
          3 Levels
        </option>
      </select>
      <LabelValidation
        show={showNumLevelsValidation}
        style={{ marginBottom: '1rem' }}
      >
        Please enter the number of levels.
      </LabelValidation>

      <ButtonSubmit onClick={() => submit()} text="Add Plan" />
    </FormPanel>
  );
};
